import { useEffect } from 'react';
import type { UseAddItemToRegistryAttemptReturn } from '@apps/registry/public/routes/Shop/useAddItemToRegistryAttempt';
import { UseSharedCatalogMutationReturn } from '../Catalog.controller';
import { CashFundDialogContext } from '../components/CashFundDialogProvider';
import { CASH_FUNDS_ALIASES } from '@apps/registry/common/utils/departmentAliases';
import { CustomItemDataType } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

type UseRetryAddCatalogItemAttemptArgs = Readonly<{
  eventId: Maybe<string>;
  addToRegistryAttemptInfo: Maybe<UseAddItemToRegistryAttemptReturn['addToRegistryAttemptInfo']>;
  addCatalogItemToRegistry: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  openAddCatalogCashFundDialog: CashFundDialogContext['openAddCatalogCashFundDialog'];
  openCustomCashFundDialog: CashFundDialogContext['openCustomCashFundDialog'];
  removeAddAttemptInfo: Maybe<() => void>;
}>;

// Taken from existing
const CASH_FUNDS_CATEGORIES = CASH_FUNDS_ALIASES.reduce(
  (cashFundCategories, cashFundCategoryName) => ({ ...cashFundCategories, [cashFundCategoryName]: CustomItemDataType.cash }),
  {}
);

const CATEGORY_TITLE_TO_FUND_TYPE = {
  ...CASH_FUNDS_CATEGORIES,
  Charity: CustomItemDataType.charity
} as const;

type CategoryTitleToFundTypeKey = keyof typeof CATEGORY_TITLE_TO_FUND_TYPE;
/**
 * When a user has not chosen an event but tries to add a catalog item, we hold onto this add attempt payload.
 * After they select an event from their event list, we try to add their previous item
 */
export const useRetryAddCatalogItemAttempt = ({
  addCatalogItemToRegistry,
  addToRegistryAttemptInfo,
  eventId,
  openCustomCashFundDialog,
  openAddCatalogCashFundDialog,
  removeAddAttemptInfo
}: UseRetryAddCatalogItemAttemptArgs) => {
  const handleRetry = useEventCallback(() => {
    if (!eventId || !addToRegistryAttemptInfo) {
      return;
    }

    switch (addToRegistryAttemptInfo.type) {
      // Adding from PDP page
      case 'item': {
        const { type, ...payload } = addToRegistryAttemptInfo;
        addCatalogItemToRegistry({ source: 'catalog', payload });

        // No need to call `removeAddAttempt` here because a successfull `addCatalogItemToRegistry` will
        // call `onAddCatalogItemSuccess` (which itself calls `removeAddAttempt`)
        break;
      }

      // Adding from product grid (quick add, pdp modal)
      case 'catalog': {
        if (addToRegistryAttemptInfo.itemType === 'product') {
          const { type, ...payload } = addToRegistryAttemptInfo;
          addCatalogItemToRegistry({ source: 'catalog', payload });
        } else if (addToRegistryAttemptInfo.itemType === 'cash' || addToRegistryAttemptInfo.itemType === 'charity') {
          // Cash fund + Custom Cash Fund
          // always remove the attempt
          const { type, ...payload } = addToRegistryAttemptInfo;
          if (payload.isCreateFromCustom) {
            openCustomCashFundDialog({
              addMethodType: 'custom',
              fundType: CATEGORY_TITLE_TO_FUND_TYPE[payload.category?.title as CategoryTitleToFundTypeKey]
            });
          } else if (payload.product) {
            openAddCatalogCashFundDialog({ addMethodType: payload.telemetryArgs.method, product: payload.product, itemPosition: payload.itemPosition });
          }

          // Remove attempt because most (if not all) cash fund/charity flows are multi-step
          removeAddAttemptInfo?.();
        }

        break;
      }

      case 'universalSearch': {
        addCatalogItemToRegistry({ source: 'universalSearch', payload: addToRegistryAttemptInfo });
        break;
      }
    }
  });

  useEffect(() => {
    handleRetry();
  }, [addToRegistryAttemptInfo, eventId, handleRetry]);
};
